import React, { useEffect, useState, useCallback, useRef } from "react";

const TICKER_SYMBOLS = [
  { sym: "^NSEI", label: "NIFTY 50" },
  { sym: "^NSEBANK", label: "BANK NIFTY" },
  { sym: "^CNXMIDCAP", label: "NIFTY MIDCAP 100" },
  { sym: "^CNXSMALLCAP", label: "NIFTY SMALLCAP 100" },
  { sym: "RELIANCE.NS", label: "RELIANCE" },
  { sym: "HDFCBANK.NS", label: "HDFC BANK" },
  { sym: "INFY.NS", label: "INFOSYS" },
  { sym: "TCS.NS", label: "TCS" },
  { sym: "ICICIBANK.NS", label: "ICICI BANK" },
  { sym: "SBIN.NS", label: "SBI" },
];

const CACHE_KEY = "market-ticker-cache-v1";

const fmt = (n, d = 2) =>
  typeof n === "number" ? n.toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—";

function TickerItem({ q }) {
  const change = q.change;
  const pctVal = q.change_pct;
  const color = typeof change === "number"
    ? (change > 0 ? "text-emerald-400" : change < 0 ? "text-rose-400" : "text-zinc-400")
    : "text-zinc-500";
  const arrow = typeof change === "number"
    ? (change > 0 ? "▲" : change < 0 ? "▼" : "■")
    : "";
  const priceText = q.price != null ? fmt(q.price) : "—";
  return (
    <span className="inline-flex items-center gap-2 px-5 text-sm whitespace-nowrap" data-testid={`ticker-${q.label}`}>
      <span className="font-semibold text-zinc-100">{q.label}</span>
      <span className="font-mono text-zinc-100">{priceText}</span>
      {q.price != null && typeof change === "number" ? (
        <span className={`font-mono text-xs ${color}`}>
          {arrow} {fmt(Math.abs(change))} ({pctVal >= 0 ? "+" : ""}{fmt(pctVal, 2)}%)
        </span>
      ) : (
        <span className="text-zinc-500 text-xs" title="Price unavailable for this symbol">⚠</span>
      )}
      <span className="text-zinc-700 ml-3">|</span>
    </span>
  );
}

const STATUS_MAP = {
  OPEN: { dot: "bg-emerald-400", label: "Market Open", color: "text-emerald-300" },
  PRE_OPEN: { dot: "bg-amber-400", label: "Pre-Open Session", color: "text-amber-300" },
  CLOSED: { dot: "bg-rose-400", label: "Market Closed", color: "text-rose-300" },
};

function StatusPill({ status, isStale }) {
  if (!status) return null;
  const s = STATUS_MAP[status] || STATUS_MAP.CLOSED;
  return (
    <div className="flex items-center gap-2 px-4 border-l border-zinc-700 text-xs whitespace-nowrap" data-testid="market-status-badge">
      <span className={`w-2 h-2 rounded-full ${s.dot} ${status === "OPEN" ? "animate-pulse" : ""}`}></span>
      <span className={s.color}>{s.label}</span>
      {isStale && <span className="text-zinc-500 ml-1">· cached</span>}
    </div>
  );
}

const loadCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.quotes) ? parsed : null;
  } catch { return null; }
};

const saveCache = (quotes) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ quotes, saved_at: Date.now() }));
  } catch { /* quota / disabled */ }
};

export default function MarketTicker() {
  const cached = useRef(loadCache());
  const [quotes, setQuotes] = useState(() => (cached.current?.quotes || []));
  const [status, setStatus] = useState(null);
  const [isStale, setIsStale] = useState(!!cached.current);

  const load = useCallback(async () => {
    try {
      const symbols = TICKER_SYMBOLS.map((t) => t.sym).join(",");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stock-prices?symbols=${encodeURIComponent(symbols)}`);
      const data = await r.json();
      const byPrefix = {};
      (data.quotes || []).forEach((q) => {
        const base = (q.symbol || "").replace(/\.(NS|BO)$/, "");
        byPrefix[base] = q;
      });
      // Merge with previous cache so a partial failure never blanks the ticker
      const prevByLabel = Object.fromEntries((cached.current?.quotes || []).map((q) => [q.label, q]));
      const merged = TICKER_SYMBOLS.map((t) => {
        const base = t.sym.replace(/\.(NS|BO)$/, "");
        const fresh = byPrefix[base];
        const prev = prevByLabel[t.label] || {};
        // Prefer fresh price; fall back to previously known good price
        if (fresh?.price != null) return { ...fresh, label: t.label };
        return { ...prev, ...fresh, label: t.label, price: prev.price, change: prev.change, change_pct: prev.change_pct, stale: true };
      });
      const anyFresh = merged.some((q) => !q.stale && q.price != null);
      setQuotes(merged);
      setIsStale(!anyFresh && merged.some((q) => q.price != null));
      if (anyFresh) {
        cached.current = { quotes: merged };
        saveCache(merged);
      }
    } catch {/* keep prior quotes */}
    try {
      const m = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market-status`);
      const j = await m.json();
      setStatus(j.status);
    } catch {/* swallow */}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (!quotes.length) return null;

  return (
    <div className="bg-zinc-950 text-zinc-100 border-b border-zinc-800 overflow-hidden flex items-stretch" data-testid="market-ticker">
      <div className="flex-1 overflow-hidden relative">
        <div className="flex animate-marquee py-2.5" style={{ width: "max-content" }}>
          {[...quotes, ...quotes].map((q, i) => (
            <TickerItem key={`${q.label}-${i}`} q={q} />
          ))}
        </div>
      </div>
      <StatusPill status={status} isStale={isStale} />
    </div>
  );
}
